// Mission Table Row component - Table row with expandable task details
// CHANGE: Removed all debug console statements for production
// CREATED: Displays mission information in a table row format with expand/collapse functionality
// Loads tasks on-demand when expanded to optimize performance

import { useState } from 'react';
import { ChevronDown, Repeat, Check } from 'lucide-react';
import { formatDuration } from '../../lib/dateUtils';
import { getServiceColorClass } from '../../lib/services';
import { formatRecurrenceText } from '../../lib/recurrence';
import { getTemplateWithItems, type TemplateWithItems } from '../../lib/templates';
import { Tooltip } from '../ui/Tooltip';
import { DropdownMenu } from '../ui/DropdownMenu';
import type { DropdownMenuItem } from '../ui/DropdownMenu';
import type { TaskItem } from '../../lib/supabase';

interface MissionTableRowProps {
  template: TemplateWithItems;
  isExpanded: boolean;
  onToggleExpand: () => void;
  dropdownItems: DropdownMenuItem[];
}

export function MissionTableRow({ template, isExpanded, onToggleExpand, dropdownItems }: MissionTableRowProps) {
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleExpand = async () => {
    if (isExpanded) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before collapsing
      setTimeout(() => {
        setIsClosing(false);
        onToggleExpand();
      }, 300); // Match animation duration
    } else {
      // Expanding - load tasks if needed
      if (!tasksLoaded) {
        setIsLoadingTasks(true);
        try {
          const templateWithItems = await getTemplateWithItems(template.id);
          if (templateWithItems?.items) {
            setTasks(templateWithItems.items);
            setTasksLoaded(true);
          }
        } catch (error) {
        } finally {
          setIsLoadingTasks(false);
        }
      }
      onToggleExpand();
    }
  };

  return (
    <>
      {/* Main row */}
      <tr className="hover:bg-gray-50 transition-colors border-b border-gray-100">
        {/* Chevron + Mission name */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleExpand}
              className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
              aria-label={isExpanded ? 'Réduire' : 'Développer'}
            >
              <ChevronDown
                size={20}
                className={`text-gray-600 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">{template.name}</span>
              {template.recurrence && (
                <Tooltip content={formatRecurrenceText(template.recurrence)} position="top">
                  <div className="inline-flex items-center justify-center w-5 h-5 bg-green-100 rounded-full cursor-pointer flex-shrink-0">
                    <Repeat size={12} className="text-green-700" />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </td>

        {/* Service */}
        <td className="px-6 py-4">
          {template.service ? (
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getServiceColorClass(template.service.color)}`}
            >
              {template.service.name}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">Sans service</span>
          )}
        </td>

        {/* Duration */}
        <td className="px-6 py-4">
          <span className="text-sm text-gray-700">
            {template.estimated_duration_minutes ? (
              formatDuration(template.estimated_duration_minutes)
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </td>

        {/* Actions */}
        <td className="px-6 py-4 text-right">
          <div className="flex justify-end">
            {dropdownItems.length > 0 && (
              <DropdownMenu items={dropdownItems} align="right" />
            )}
          </div>
        </td>
      </tr>

      {/* Expanded row with tasks */}
      {(isExpanded || isClosing) && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <div className={`border-b border-gray-100 px-6 py-4 bg-gray-50 ${isClosing ? 'animate-collapseSection' : 'animate-expandSection'}`}>
              {/* Tasks list */}
              {isLoadingTasks ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : tasks.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 uppercase mb-3">
                    Tâches ({tasks.length})
                  </div>
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-700">{task.title}</span>
                        {task.is_text_input_required && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Saisie obligatoire
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  Aucune tâche définie
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
